import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraOverlayDialog, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { useCapabilityLinkMutations, useCapabilityLinks, useCapabilityLinkTargets, useLinkCapabilities } from './hooks';
const targetKey = (t)=>[
        t.targetKind,
        t.rfiQuestionId,
        t.proposalProjectId,
        t.proposalRequirementId
    ].join(':');
export function LinkRequirementDialog(props) {
    return props.open ? /*#__PURE__*/ _jsx(RequirementForm, {
        ...props
    }) : null;
}
function RequirementForm({ onOpenChange, capabilityId, presetTarget, onLinked }) {
    const [capability, setCapability] = useState(capabilityId ?? '');
    const [target, setTarget] = useState(presetTarget);
    const [query, setQuery] = useState('');
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const busy = useRef(false);
    const capabilities = useLinkCapabilities(!capabilityId);
    const permission = useCapabilityLinks(capability);
    const targets = useCapabilityLinkTargets(query, !presetTarget);
    const { link } = useCapabilityLinkMutations();
    const toOption = (t)=>({
            value: targetKey(t),
            label: t.label,
            description: [
                t.contextName,
                t.excerpt
            ].filter(Boolean).join(' · ')
        });
    const close = (next)=>{
        if (!busy.current) onOpenChange(next);
    };
    const allowed = permission.data?.canLink === true && !permission.isError;
    const submit = async ()=>{
        if (busy.current || !allowed || !capability || !target || Array.from(note).length > 500) return;
        busy.current = true;
        setError('');
        try {
            const response = await link.mutateAsync({
                capabilityId: capability,
                targetKind: target.targetKind,
                rfiQuestionId: target.rfiQuestionId,
                proposalProjectId: target.proposalProjectId,
                proposalRequirementId: target.proposalRequirementId,
                note
            });
            busy.current = false;
            onOpenChange(false);
            if (response.link?.askedForIn) onLinked?.(response.link.askedForIn);
        } catch (e) {
            setError(apiErrorMessage(e, 'Could not link this question. Try again.'));
        } finally{
            busy.current = false;
        }
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: close,
        size: "md",
        title: "Link a question or requirement",
        testId: Testids.registryCapabilities.linkDialog,
        footerProps: {
            confirmLabel: 'Link',
            loadingLabel: 'Linking…',
            loading: link.isPending,
            confirmDisabled: !target || !capability || !allowed || link.isPending || Array.from(note).length > 500,
            onConfirm: ()=>void submit(),
            onCancel: ()=>close(false),
            cancelProps: {
                disabled: link.isPending
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-[var(--space-3)]",
            children: [
                !capabilityId && /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Capability",
                    children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Capability",
                        required: true,
                        value: capability,
                        options: (capabilities.data?.capabilities ?? []).map((c)=>({
                                value: c.id,
                                label: c.name
                            })),
                        onValueChange: (id)=>setCapability(id ?? ''),
                        loading: capabilities.isFetching,
                        disabled: link.isPending
                    })
                }),
                capabilities.isError && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    onClick: ()=>void capabilities.refetch(),
                    children: "Retry loading capabilities"
                }),
                permission.isError && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    onClick: ()=>void permission.refetch(),
                    children: "Retry checking link permission"
                }),
                capability && !permission.isPending && !permission.isError && !allowed && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    children: "You cannot link questions to this capability."
                }),
                presetTarget ? /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    children: [
                        presetTarget.label,
                        " · ",
                        presetTarget.contextName
                    ]
                }) : /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Find",
                    children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Find",
                        required: true,
                        value: target ? targetKey(target) : undefined,
                        selectedOption: target ? toOption(target) : undefined,
                        options: (targets.data?.targets ?? []).map(toOption),
                        onValueChange: (id)=>setTarget(targets.data?.targets.find((t)=>targetKey(t) === id)),
                        onSearchValueChange: setQuery,
                        shouldFilter: false,
                        loading: targets.isFetching || targets.settling,
                        disabled: link.isPending,
                        emptyMessage: "No questions or requirements match."
                    })
                }),
                targets.isError && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    onClick: ()=>void targets.refetch(),
                    children: "Retry searching questions"
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Note (optional)",
                    error: Array.from(note).length > 500 ? 'Use at most 500 characters.' : undefined,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        "aria-label": "Note",
                        maxLength: 500,
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        disabled: link.isPending
                    })
                }),
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    children: error
                })
            ]
        })
    });
}
